<template>
  <div class="w-full flex justify-center bg-themeComponentBg-light font-rubik py-8">
    <div class="input-outer-container mx-5">
      <div class="mt-12 flex flex-row justify-between items-center gap-1 custom-shadow">
        <div class="input-container flex flex-row justify-around items-center">
          <div class="flex items-center gap-1">
            <geolocate
              class="mb-2"
              @success="searchByCoordinates"
              :showIcon="false"
              :onlyIcon="true"
            />
            <img
              v-if="loading"
              class="animate-spin h-4"
              src="@/images/spinner.svg"
              alt="Loading"
            >
          </div>

          <input
            @keyup.enter="search"
            v-model="query"
            @click="coordinates = null"
            :class="{ 'is-danger': error }"
            class="bg-transparent w-full focus:ring-0 focus:outline-none"
            type="search"
            placeholder="Where are you located? Try entering your City or Zipcode"
          />
        </div>

        <div>
          <button
            @click="search"
            class="location-search-btn flex justify-center items-center"
            :disabled="isButtonDisabled"
          >
            <img src="@/images/user-location/search-home.svg" alt="Search icon">
          </button>
        </div>
      </div>

      <div>
        <!-- error message -->
        <div
          v-if="error"
          class="help py-1 text-center bg-white text-red-500 border rounded-md"
        >
          {{ error }}
          <b><a @click.prevent="resetSearch()">Try Again</a></b>
        </div>

        <!-- results list (overlays content below) -->
        <div
          v-if="cities.length"
          role="menu"
          class="results-dropdown border rounded-md"
        >
          <div
            class="dropdown-content modal-card-body"
            style="max-height: 300px !important;"
          >
            <p class="dropdown-item"><b>We found mulitple results...</b></p>
            <a
              v-for="city in cities"
              :key="city.id"
              :href="cityPath(city.node)"
              class="dropdown-item"
            >
              {{ city.node.name }}, {{ city.node.stateId | upcase }}
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ahoy from 'shared/ahoy'
import Geolocate from '../location-search/geolocate.vue'
import { zipQuery, citySearchQuery, geoSearchQuery } from 'shared/graphql/queries'
import { graphqlQuery } from 'shared/graphql'
import { cityPath } from '../../shared/city-helper'
import { partnerPath } from '../../shared/partner-helper'
import { mapState } from 'vuex'

const ZIP_REGEX = /^\d{5}$/
const redirectToCity    = (city)  => (window.location.href = cityPath(city))
const redirectToPartner = (organization) => (window.location.href = partnerPath(organization))

export default {
  components: { Geolocate },

  data() {
    return {
      loading: 0,
      error: null,
      query: '',
      coordinates: null,
      cities: [],
      lastQuery: ''
    }
  },

  filters: {
    upcase(str) {
      return str.toUpperCase()
    }
  },

  computed: {
    ...mapState('shared', ['zipcode']),
    ...mapState('quote',  ['lead']),
    isButtonDisabled() {
      return !/^(\d{5})|(\D{3,})$/i.test(this.query)
    }
  },

  watch: {
    query() {
      if (this.query === '') {
        this.error = null
        this.cities = []
      } else if (this.error && this.query !== this.lastQuery) {
        this.error = null
      }
    }
  },

  mounted() {
    this.loading = 0
    this.query   = this.zipcode
  },

  methods: {
    cityPath,
    searchByCoordinates(coords) {
      this.coordinates = coords
      this.query = ''
      this.search()
    },
    resetSearch() {
      this.query = ''
      this.error = null
    },
    getRequestBody() {
      if (ZIP_REGEX.test(this.query)) {
        return { query: zipQuery, variables: { id: this.query } }
      }
      if (this.coordinates) {
        return { query: geoSearchQuery, variables: { coordinates: this.coordinates } }
      }
      const cityOnly = this.query.replace(/[, ]+[a-z]{2}\s*$/i, '').trim()
      return { query: citySearchQuery, variables: { query: cityOnly } }
    },
    async trackSearch() {
      ahoy.track('location.search', { coordinates: this.coordinates, query: this.query })
    },

    async search() {
      this.cities    = []
      this.loading++
      this.lastQuery = this.query = this.query.trim()

      try {
        this.trackSearch()
        const result = await graphqlQuery(this.getRequestBody())

        if (result.data.zipcode) {
          const aboutSlug = `/service-areas/${result.data.zipcode.city.id}/`
          localStorage.setItem('serviceAreaAboutSlug', JSON.stringify(aboutSlug))
        }
        return this.handleResult(result.data || {})
      } catch (e) {
        console.warn(e)
        this.error = 'There was an error while searching.'
        this.loading = 0
      }
    },

    async handleResult(res) {
      let { zipcode, zipcodes, cities } = res
      if (!zipcode && zipcodes?.edges?.length === 1) zipcode = zipcodes.edges[0].node

      if (zipcode) {
        if (this.lead) {
          await this.$store.dispatch('quote/lead.zipcode.update', { lead: this.lead, zipcode: zipcode.id })
        } else {
          this.$store.commit('shared/zipcode.set', zipcode.id)
        }
        const org = zipcode.city?.organization?.id
        if (org) return redirectToPartner(org)
        return redirectToCity(zipcode.city)
      }

      if (cities?.edges?.length) {
        const stateToken = (this.query.match(/([a-z]{2})\s*$/i) || [])[1]?.toLowerCase() || null
        const baseName   = this.query.replace(/[, ]+[a-z]{2}\s*$/i, '').trim().toLowerCase()

        const ranked = cities.edges.map(e => {
          const n = e.node
          const exact   = n.name.toLowerCase() === baseName
          const inState = stateToken && n.stateId.toLowerCase() === stateToken
          let rank = 3
          if (exact)        rank = inState ? 0 : 1
          else if (inState) rank = 2
          return { rank, edge: e }
        })

        this.cities = ranked.sort((a, b) => a.rank - b.rank).map(r => r.edge)
        this.loading = 0

        if (this.cities.length === 1 && ranked[0].rank === 0) {
          const n = this.cities[0].node
          return n.organization?.id ? redirectToPartner(n.organization.id) : redirectToCity(n)
        }
        return
      }

      this.error = 'Sorry, we couldn’t find that location.'
      this.loading = 0
    }

  }
}
</script>

<style scoped>
.input-outer-container {
  position: relative;
  width: 100%;
  max-width: 910px;
}

.input-container {
  width: 100%;
  max-width: 880px;
  height: 44px;
  padding: 18px 10px;
  gap: 10px;
  border-radius: 8px;
  border: 1px transparent;
  background-color: #fff;
}

.location-search-btn {
  width: 44px;
  height: 44px;
  border: 1px transparent;
  border-radius: 8px;
  background-color: #B9C9FE;
}

/* floating dropdown */
.results-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 100%;
  z-index: 1000;
  background: #fff;
  max-height: 300px;
  /* overflow-y: auto; */
  border-radius: 8px;
}

.custom-shadow {
  position: relative;
  z-index: 10;
  box-shadow:
    0px 22px 48px 0px #4D84FF1A,
    0px 196px 117px 0px #4D84FF1A,
    0px 348px 139px 0px #4D84FF08,
    0px 543px 152px 0px #4D84FF00;
}
</style>
